import React, { useState, useMemo } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Button, 
  Table, 
  Badge, 
  ButtonGroup,
  FormControl,
  InputGroup,
  Dropdown,
  Nav,
  Tab,
  Modal,
  Form
} from 'react-bootstrap';

// Types
interface Application {
  id: number;
  job_id: number;
  job_title: string;
  company_name: string;
  location: string;
  salary_range?: string;
  application_date: string;
  status: 'pending' | 'reviewed' | 'interview_scheduled' | 'interview_completed' | 'offer_extended' | 'hired' | 'rejected' | 'withdrawn';
  priority: 'low' | 'medium' | 'high';
}

interface ManageApplicationsProps {
  applications: Application[];
  authenticity_token?: string;
  edit_profile_path?: string;
  onApplicationUpdate?: (applicationId: number, updates: Partial<Application>) => void;
}

// Status configurations
const statusConfig = {
  pending: { color: 'warning', icon: 'fas fa-clock', label: 'Pending Review' },
  reviewed: { color: 'info', icon: 'fas fa-eye', label: 'Under Review' },
  interview_scheduled: { color: 'primary', icon: 'fas fa-calendar', label: 'Interview Scheduled' },
  interview_completed: { color: 'secondary', icon: 'fas fa-check-circle', label: 'Interview Completed' },
  offer_extended: { color: 'success', icon: 'fas fa-trending-up', label: 'Offer Extended' },
  hired: { color: 'success', icon: 'fas fa-check-circle', label: 'Hired' },
  rejected: { color: 'danger', icon: 'fas fa-times-circle', label: 'Not Selected' },
  withdrawn: { color: 'secondary', icon: 'fas fa-undo', label: 'Withdrawn' }
};

const ManageApplications: React.FC<ManageApplicationsProps> = ({
  applications = [],
  authenticity_token,
  edit_profile_path,
  onApplicationUpdate
}) => {
  // State management
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<keyof Application>('application_date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [activeTab, setActiveTab] = useState('applications');
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState<Application | null>(null);

  // Calculate statistics
  const stats = useMemo(() => {
    const total = applications.length;
    const statusCounts = applications.reduce((acc, app) => {
      acc[app.status] = (acc[app.status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return {
      total,
      pending: statusCounts.pending || 0,
      reviewed: statusCounts.reviewed || 0,
      interviews: (statusCounts.interview_scheduled || 0) + (statusCounts.interview_completed || 0),
      hired: statusCounts.hired || 0,
      rejected: statusCounts.rejected || 0
    };
  }, [applications]);

  // Filter and sort applications
  const filteredApplications = useMemo(() => {
    let filtered = applications.filter(app => {
      const matchesSearch = !searchTerm || 
        app.job_title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.location.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || app.status === statusFilter;
      
      return matchesSearch && matchesStatus;
    });

    // Sort applications
    filtered.sort((a, b) => {
      const aVal = a[sortBy];
      const bVal = b[sortBy];
      
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortOrder === 'asc' 
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      
      return 0;
    });

    return filtered;
  }, [applications, searchTerm, statusFilter, sortBy, sortOrder]);

  // Handle sorting
  const handleSort = (field: keyof Application) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  // Handle status update
  const handleStatusUpdate = (application: Application, newStatus: Application['status']) => {
    if (onApplicationUpdate) {
      onApplicationUpdate(application.id, { status: newStatus });
    }
    setShowStatusModal(false);
    setSelectedApplication(null);
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Calculate days since application
  const getDaysSince = (dateString: string) => {
    const days = Math.floor((Date.now() - new Date(dateString).getTime()) / (1000 * 60 * 60 * 24));
    return days;
  };

  // Empty state
  if (applications.length === 0) {
    return (
      <Container fluid>
        <Row>
          <Col>
            <Card className="text-center py-5">
              <Card.Body>
                <div className="mb-4">
                  <i className="fas fa-briefcase fa-3x text-muted mb-3"></i>
                  <h4 className="mb-3">No Applications Yet</h4>
                  <p className="text-muted mb-4">
                    You haven't applied to any jobs yet. Start browsing and apply instantly using your profile.
                  </p>
                </div>
                <Button 
                  href="/jobs" 
                  variant="primary" 
                  size="lg"
                  className="d-inline-flex align-items-center gap-2"
                >
                  <i className="fas fa-search me-2"></i>
                  Search Jobs
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid>
      <Row>
        <Col>
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h1 className="h2 mb-1">My Job Applications</h1>
              <p className="text-muted mb-0">
                Track the status of jobs you've applied for and manage your application pipeline.
              </p>
            </div>
          </div>

          {/* Analytics Tab Navigation */}
          <Nav variant="tabs" activeKey={activeTab} onSelect={setActiveTab} className="mb-4">
            <Nav.Item>
              <Nav.Link eventKey="applications">
                Applications ({stats.total})
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="analytics">
                Analytics
              </Nav.Link>
            </Nav.Item>
          </Nav>

          <Tab.Content>
            {/* Analytics Tab */}
            {activeTab === 'analytics' && (
              <Tab.Pane eventKey="analytics">
                <Row className="g-4 mb-4">
                  <Col md={3}>
                    <Card>
                      <Card.Body className="text-center">
                        <i className="fas fa-chart-bar fa-2x text-primary mb-2"></i>
                        <h3 className="h4 mb-1">{stats.total}</h3>
                        <p className="text-muted mb-0">Total Applications</p>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card>
                      <Card.Body className="text-center">
                        <i className="fas fa-calendar fa-2x text-info mb-2"></i>
                        <h3 className="h4 mb-1">{stats.interviews}</h3>
                        <p className="text-muted mb-0">Interviews</p>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card>
                      <Card.Body className="text-center">
                        <i className="fas fa-check-circle fa-2x text-success mb-2"></i>
                        <h3 className="h4 mb-1">{stats.hired}</h3>
                        <p className="text-muted mb-0">Hired</p>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card>
                      <Card.Body className="text-center">
                        <i className="fas fa-times-circle fa-2x text-danger mb-2"></i>
                        <h3 className="h4 mb-1">{stats.rejected}</h3>
                        <p className="text-muted mb-0">Rejected</p>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </Tab.Pane>
            )}

            {/* Applications Tab */}
            <Tab.Pane eventKey="applications">
              {/* Filters and Search */}
              <Card className="mb-4">
                <Card.Body>
                  <Row className="g-3">
                    <Col md={4}>
                      <InputGroup>
                        <InputGroup.Text>
                          <i className="fas fa-search"></i>
                        </InputGroup.Text>
                        <FormControl
                          placeholder="Search by job title, company, or location..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </InputGroup>
                    </Col>
                    <Col md={3}>
                      <Form.Select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                      >
                        <option value="all">All Statuses</option>
                        {Object.entries(statusConfig).map(([status, config]) => (
                          <option key={status} value={status}>
                            {config.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                    <Col md={5} className="text-end">
                      <span className="text-muted">
                        {filteredApplications.length} of {applications.length} applications
                      </span>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Applications Table */}
              <Card>
                <div className="table-responsive">
                  <Table hover className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>
                          <Button
                            variant="link"
                            className="p-0 text-decoration-none text-dark fw-bold"
                            onClick={() => handleSort('job_title')}
                          >
                            Job Title
                            {sortBy === 'job_title' && (
                              <i className={`fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'} ms-1`}></i>
                            )}
                          </Button>
                        </th>
                        <th>Company</th>
                        <th>Location</th>
                        <th>
                          <Button
                            variant="link"
                            className="p-0 text-decoration-none text-dark fw-bold"
                            onClick={() => handleSort('application_date')}
                          >
                            Applied
                            {sortBy === 'application_date' && (
                              <i className={`fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'} ms-1`}></i>
                            )}
                          </Button>
                        </th>
                        <th>Status</th>
                        <th>Priority</th>
                        <th style={{ width: '100px' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredApplications.map((application) => {
                        const config = statusConfig[application.status];
                        const daysSince = getDaysSince(application.application_date);
                        
                        return (
                          <tr key={application.id}>
                            <td>
                              <div>
                                <strong>{application.job_title}</strong>
                                {application.salary_range && (
                                  <div className="small text-muted">{application.salary_range}</div>
                                )}
                              </div>
                            </td>
                            <td>
                              <div className="d-flex align-items-center">
                                <i className="fas fa-building text-muted me-2"></i>
                                {application.company_name}
                              </div>
                            </td>
                            <td>
                              <div className="d-flex align-items-center">
                                <i className="fas fa-map-marker-alt text-muted me-2"></i>
                                {application.location}
                              </div>
                            </td>
                            <td>
                              <div>
                                {formatDate(application.application_date)}
                                <div className="small text-muted">
                                  {daysSince === 0 ? 'Today' : `${daysSince} days ago`}
                                </div>
                              </div>
                            </td>
                            <td>
                              <Badge 
                                bg={config.color}
                                className="d-flex align-items-center gap-1"
                                style={{ width: 'fit-content' }}
                              >
                                <i className={config.icon}></i>
                                {config.label}
                              </Badge>
                            </td>
                            <td>
                              <Badge 
                                bg={application.priority === 'high' ? 'danger' : 
                                    application.priority === 'medium' ? 'warning' : 'secondary'}
                              >
                                {application.priority}
                              </Badge>
                            </td>
                            <td>
                              <Dropdown>
                                <Dropdown.Toggle as="button" type="button" className="context-menu-toggle" aria-label="More actions">
                                  <i className="fas fa-ellipsis-v" aria-hidden="true"></i>
                                </Dropdown.Toggle>
                                <Dropdown.Menu align="end" className="context-menu">
                                  <Dropdown.Item
                                    as="button"
                                    type="button"
                                    className="context-menu__item"
                                    onClick={() => {
                                      setSelectedApplication(application);
                                      setShowStatusModal(true);
                                    }}
                                  >
                                    <i className="fas fa-edit" aria-hidden="true"></i>
                                    Update Status
                                  </Dropdown.Item>
                                  <Dropdown.Item className="context-menu__item" href={`/jobs/${application.job_id}`}>
                                    <i className="fas fa-eye" aria-hidden="true"></i>
                                    View Job
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              </Card>
            </Tab.Pane>
          </Tab.Content>

          {/* Status Update Modal */}
          <Modal show={showStatusModal} onHide={() => setShowStatusModal(false)} centered>
            <Modal.Header closeButton>
              <Modal.Title>Update Application Status</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {selectedApplication && (
                <div>
                  <h6 className="mb-3">{selectedApplication.job_title}</h6>
                  <p className="text-muted mb-4">{selectedApplication.company_name}</p>
                  
                  <div className="d-grid gap-2">
                    {Object.entries(statusConfig).map(([status, config]) => {
                      const isActive = selectedApplication.status === status;
                      
                      return (
                        <Button
                          key={status}
                          variant={isActive ? config.color : `outline-${config.color}`}
                          className="d-flex align-items-center justify-content-start gap-3 p-3"
                          onClick={() => handleStatusUpdate(selectedApplication, status as Application['status'])}
                        >
                          <i className={config.icon}></i>
                          <div className="text-start">
                            <div className="fw-bold">{config.label}</div>
                          </div>
                        </Button>
                      );
                    })}
                  </div>
                </div>
              )}
            </Modal.Body>
          </Modal>
        </Col>
      </Row>
    </Container>
  );
};

export default ManageApplications;
